import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiTranslateLoader } from './api-translate-loader.service';
import { Router } from '@angular/router';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  // Danh sách ngôn ngữ được hỗ trợ
  private languages: Language[] = [
    {
      code: 'vi',
      name: 'Tiếng Việt',
      nativeName: 'Tiếng Việt',
      flag: 'assets/images/flags/vn.svg'
    },
    {
      code: 'en',
      name: 'English',
      nativeName: 'English (Beta)',
      flag: 'assets/images/flags/us.svg'
    },
    {
      code: 'cn',
      name: 'Chinese',
      nativeName: '中文 (公测版)',
      flag: 'assets/images/flags/cn.svg'
    }
  ];

  private currentLanguageSubject = new BehaviorSubject<Language>(this.languages[0]);
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private apiLoader: ApiTranslateLoader,
    private router: Router
  ) {
    // Khởi tạo với ngôn ngữ đã lưu hoặc mặc định
    this.initLanguage();
  }

  /**
   * Khởi tạo ngôn ngữ từ localStorage hoặc sử dụng mặc định
   */
  private initLanguage(): void {
    const savedLang = localStorage.getItem('app_language') || 'en';
    this.changeLanguage(savedLang);
  }

  /**
   * Lấy danh sách ngôn ngữ được hỗ trợ
   */
  getLanguages(): Language[] {
    return [...this.languages];
  }

  /**
   * Đổi ngôn ngữ hiện tại
   * @param langCode Mã ngôn ngữ (vi, en, cn)
   */
  changeLanguage(langCode: string): void {
    // Kiểm tra xem có hỗ trợ ngôn ngữ này không
    const language = this.languages.find(lang => lang.code === langCode);
    if (!language) {
      console.error(`Language ${langCode} is not supported`);
      return;
    }

    this.isLoadingSubject.next(true);

    // Lưu lựa chọn ngôn ngữ vào localStorage
    localStorage.setItem('app_language', langCode);

    // Đặt ngôn ngữ hiện tại cho TranslateService
    this.translate.use(langCode).subscribe({
      next: () => {
        // Cập nhật ngôn ngữ hiện tại trong service
        this.currentLanguageSubject.next(language);
        this.isLoadingSubject.next(false);
      },
      error: (err) => {
        console.error('Error changing language:', err);
        this.isLoadingSubject.next(false);
      }
    });
  }

  /**
   * Lấy ngôn ngữ hiện tại
   */
  getCurrentLanguage(): Language {
    return this.currentLanguageSubject.getValue();
  }

  /**
   * Cập nhật lại bản dịch từ API (xóa cache)
   * @param langCode Mã ngôn ngữ cần cập nhật, nếu không có sẽ cập nhật ngôn ngữ hiện tại
   */
  reloadTranslations(langCode?: string): void {
    const lang = langCode || this.currentLanguageSubject.getValue().code;

    // Xóa cache
    this.apiLoader.clearCache(lang);

    // Tải lại bản dịch
    this.isLoadingSubject.next(true);
    this.translate.reloadLang(lang).subscribe({
      next: () => {
        this.translate.use(lang);
        this.isLoadingSubject.next(false);
      },
      error: (err) => {
        console.error('Error reloading translations:', err);
        this.isLoadingSubject.next(false);
      }
    });
  }

  resetToDefault(): void {
    const defaultLang = this.languages[0]; // vi
    this.currentLanguageSubject.next(defaultLang);
    this.translate.setDefaultLang(defaultLang.code);
    this.translate.use(defaultLang.code);
  }
}
